/**
 * Form Analytics Hook
 *
 * Tracks form views, starts, completions, field interactions, and drop-offs.
 * Used in FormRenderer to automatically record analytics.
 */

import { useEffect, useRef, useCallback } from 'react';

interface UseFormAnalyticsOptions {
  formId: string;
  totalFields: number;
  totalSteps: number;
  enabled?: boolean;
}

interface FieldInteractionData {
  fieldId: string;
  fieldName: string;
  startTime: number;
}

export function useFormAnalytics({
  formId,
  totalFields,
  totalSteps,
  enabled = true,
}: UseFormAnalyticsOptions) {
  const sessionId = useRef<string>(getOrCreateSessionId(formId));
  const startTime = useRef<number>(Date.now());
  const hasStarted = useRef(false);
  const fieldsInteracted = useRef<Set<string>>(new Set());
  const currentFieldInteraction = useRef<FieldInteractionData | null>(null);

  // Record view on mount
  useEffect(() => {
    if (!enabled) return;

    recordEvent('view', { totalFields, totalSteps });

    // Cleanup on unmount - record abandon if not completed
    return () => {
      if (hasStarted.current) {
        const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
        recordEvent('abandon', {
          fieldsInteracted: fieldsInteracted.current.size,
          timeSpentSeconds: timeSpent,
        });
      }
    };
  }, [formId, enabled, totalFields, totalSteps]);

  const recordEvent = useCallback(
    async (
      event: 'view' | 'start' | 'complete' | 'abandon' | 'field_interaction' | 'page_change',
      data?: Record<string, unknown>
    ) => {
      if (!enabled) return;

      try {
        await fetch(`/api/forms/${formId}/analytics`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event,
            sessionId: sessionId.current,
            data,
          }),
        });
      } catch (error) {
        console.error('Analytics error:', error);
      }
    },
    [formId, enabled]
  );

  const trackFieldFocus = useCallback(
    (fieldId: string, fieldName: string) => {
      if (!enabled) return;

      // Mark form as started on first field focus
      if (!hasStarted.current) {
        hasStarted.current = true;
        startTime.current = Date.now();
        recordEvent('start', { totalFields, totalSteps });
      }

      currentFieldInteraction.current = {
        fieldId,
        fieldName,
        startTime: Date.now(),
      };
    },
    [enabled, recordEvent, totalFields, totalSteps]
  );

  const trackFieldBlur = useCallback(
    (fieldId: string, fieldName: string, hadError: boolean = false) => {
      if (!enabled) return;

      fieldsInteracted.current.add(fieldId);

      const interaction = currentFieldInteraction.current;
      if (interaction && interaction.fieldId === fieldId) {
        const timeSpentMs = Date.now() - interaction.startTime;

        recordEvent('field_interaction', {
          fieldId,
          fieldName,
          interactionType: 'blur',
          timeSpentMs,
          hadError,
        });

        currentFieldInteraction.current = null;
      }
    },
    [enabled, recordEvent]
  );

  const trackPageChange = useCallback(
    (currentStep: number) => {
      if (!enabled) return;

      recordEvent('page_change', { currentStep });
    },
    [enabled, recordEvent]
  );

  const trackCompletion = useCallback(() => {
    if (!enabled) return;

    const timeSpent = Math.round((Date.now() - startTime.current) / 1000);

    recordEvent('complete', {
      fieldsInteracted: fieldsInteracted.current.size,
      totalFields,
      timeSpentSeconds: timeSpent,
    });

    // Mark as completed so we don't record abandon
    hasStarted.current = false;
  }, [enabled, recordEvent, totalFields]);

  return {
    sessionId: sessionId.current,
    trackFieldFocus,
    trackFieldBlur,
    trackPageChange,
    trackCompletion,
  };
}

// Helper to get or create a session ID for this form
function getOrCreateSessionId(formId: string): string {
  const storageKey = `form_analytics_session_${formId}`;
  let sessionId = sessionStorage.getItem(storageKey);

  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(storageKey, sessionId);
  }

  return sessionId;
}
